import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';

type JobT = {
  id: number;
  title: string;
  companyName: string;
  employmentType: string;
  workMode: string;
  location: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  industry: string | null;
  description: string;
  closingDate: string | null;
  isClosed: boolean;
  contactEmail: string | null;
  contactPhone: string | null;
  mapUrl: string | null;
  applicantCount: number;
  companyLogoUrl: string | null;
  createdAt: string;
};

const CustomerJobList: React.FC = () => {
  const { id: customerId } = useParams();
  const [jobs, setJobs] = useState<JobT[]>([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, rowCount: 0, loading: false });
  const [deleteTarget, setDeleteTarget] = useState<JobT | null>(null);
  const [editTarget, setEditTarget] = useState<JobT | null>(null);
  const [editForm, setEditForm] = useState<Partial<JobT>>({});

  // Services — URLs with :id replaced at call time via requestParams
  const { service: listService } = useServices(
    ServiceNameEnum.jobs.listByCustomer.replace(':customerId', customerId ?? ''),
  );
  const { service: closeService } = useServices(ServiceNameEnum.jobs.close);
  const { service: updateService } = useServices(ServiceNameEnum.jobs.update);
  const { service: deleteService } = useServices(ServiceNameEnum.jobs.delete);

  const loadJobs = async () => {
    setPagination((p) => ({ ...p, loading: true }));
    const { data, error } = await listService.get({
      queryString: { page: pagination.page + 1, limit: pagination.pageSize },
    });
    if (data) {
      setJobs(data.items);
      setPagination((p) => ({ ...p, rowCount: data.pagination.total, loading: false }));
    } else if (error) {
      toast.error(AxiosService.getErrorResponse(error)?.message ?? 'Failed to load jobs');
      setPagination((p) => ({ ...p, loading: false }));
    }
  };

  useEffect(() => { loadJobs(); }, [pagination.page, pagination.pageSize]);

  const handleCloseToggle = async (job: JobT) => {
    const { error } = await closeService.put({ isClosed: !job.isClosed }, { params: { id: String(job.id) } });
    if (error) { toast.error('Failed to update status'); return; }
    toast.success(job.isClosed ? 'Job reopened' : 'Job closed');
    loadJobs();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await deleteService.delete({ params: { id: String(deleteTarget.id) } });
    if (error) { toast.error('Failed to delete job'); return; }
    toast.success('Job deleted');
    setDeleteTarget(null);
    loadJobs();
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    const { error } = await updateService.put(editForm, { params: { id: String(editTarget.id) } });
    if (error) { toast.error('Failed to update job'); return; }
    toast.success('Job updated');
    setEditTarget(null);
    loadJobs();
  };

  const salaryLabel = (job: JobT) => {
    if (!job.salaryMin && !job.salaryMax) return '-';
    if (job.salaryMin && job.salaryMax) {
      return `฿${job.salaryMin.toLocaleString()} – ฿${job.salaryMax.toLocaleString()}`;
    }
    return `฿${(job.salaryMin || job.salaryMax)?.toLocaleString()}`;
  };

  const columns: any[] = [
    {
      field: 'title',
      headerName: 'Job title',
      width: 220,
      renderCell: (params: any) => (
        <Stack>
          <Typography variant="body2" fontWeight={600}>{params.row.title}</Typography>
          <Typography variant="caption" color="text.secondary">{params.row.companyName}</Typography>
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      sortable: false,
      renderCell: (params: any) => (
        <Chip
          label={params.row.isClosed ? 'Closed' : 'Open'}
          size="small"
          color={params.row.isClosed ? 'default' : 'success'}
        />
      ),
    },
    {
      field: 'workMode',
      headerName: 'Mode',
      width: 110,
      sortable: false,
      valueGetter: (params: any) => params.row.workMode ?? '-',
    },
    {
      field: 'salary',
      headerName: 'Salary',
      width: 180,
      sortable: false,
      valueGetter: (params: any) => salaryLabel(params.row),
    },
    {
      field: 'applicantCount',
      headerName: 'Applicants',
      width: 100,
      sortable: false,
    },
    {
      field: 'closingDate',
      headerName: 'Closing date',
      width: 140,
      sortable: false,
      valueGetter: (params: any) =>
        params.row.closingDate
          ? DateTimeUtils.dateFormat(new Date(params.row.closingDate))
          : '-',
    },
    {
      field: 'createdAt',
      headerName: 'Posted',
      width: 160,
      sortable: false,
      valueGetter: (params: any) =>
        DateTimeUtils.dateTimeFormat(new Date(params.row.createdAt)),
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => { setEditTarget(params.row); setEditForm(params.row); }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isClosed ? 'Reopen' : 'Close'}>
            <IconButton size="small" onClick={() => handleCloseToggle(params.row)}>
              {params.row.isClosed ? <UnlockOutlined /> : <LockOutlined />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Posted Jobs
      </Typography>

      <AlumlyDataGrid
        columns={columns}
        rows={jobs}
        rowCount={pagination.rowCount}
        loading={pagination.loading}
        page={pagination.page}
        pageSize={pagination.pageSize}
        onPageChange={(page: number) => setPagination((p) => ({ ...p, page }))}
        onPageSizeChange={(pageSize: number) =>
          setPagination((p) => ({ ...p, pageSize, page: 0 }))
        }
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &ldquo;<strong>{deleteTarget?.title}</strong>&rdquo;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Job</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Job title"
              value={editForm.title ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth size="small"
            />
            <TextField
              label="Company name"
              value={editForm.companyName ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, companyName: e.target.value }))}
              fullWidth size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Salary min"
                type="number"
                value={editForm.salaryMin ?? ''}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    salaryMin: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                fullWidth size="small"
              />
              <TextField
                label="Salary max"
                type="number"
                value={editForm.salaryMax ?? ''}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    salaryMax: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                fullWidth size="small"
              />
            </Stack>
            <TextField
              label="Location"
              value={editForm.location ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
              fullWidth size="small"
            />
            <TextField
              label="Description"
              value={editForm.description ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth size="small" multiline rows={4}
            />
            <TextField
              label="Closing date (YYYY-MM-DD)"
              value={editForm.closingDate ?? ''}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, closingDate: e.target.value || null }))
              }
              fullWidth size="small"
            />
            <TextField
              label="Contact email"
              value={editForm.contactEmail ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, contactEmail: e.target.value }))}
              fullWidth size="small"
            />
            <TextField
              label="Google Maps URL"
              value={editForm.mapUrl ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, mapUrl: e.target.value }))}
              fullWidth size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerJobList;
